import type { DbShift } from '@/lib/queries'

export type WorkforceShiftMode = 'assigned' | 'self_claim'

interface WorkforceDepartmentSummary {
  color: string | null
  defaultShiftMode: WorkforceShiftMode
  defaultShowInSchedule: boolean
  icon: string | null
  id: string
  isDefault: boolean
  isFieldOps: boolean
  name: string
  slug: string
}

interface WorkforceDepartmentSummaryLike {
  color?: string | null
  default_shift_mode?: string | null
  default_show_in_schedule?: boolean | null
  defaultShiftMode?: string | null
  defaultShowInSchedule?: boolean | null
  icon?: string | null
  id?: string
  is_default?: boolean
  is_field_ops?: boolean
  isDefault?: boolean
  isFieldOps?: boolean
  name?: string
  slug?: string
}

type WorkforceDepartmentDefaultsLike = Pick<
  WorkforceDepartmentSummaryLike,
  | 'defaultShowInSchedule'
  | 'default_show_in_schedule'
  | 'defaultShiftMode'
  | 'default_shift_mode'
  | 'isFieldOps'
  | 'is_field_ops'
>

interface WorkforceDepartmentMembershipLike {
  department?: WorkforceDepartmentSummaryLike | null
  department_id?: string
  departmentId?: string
}

export interface WorkforceMemberLike {
  custom_role_id?: string | null
  custom_roles?: {
    role_type?: string | null
  } | null
  customRole?: {
    roleType?: string | null
  } | null
  customRoleId?: string | null
  department_members?: WorkforceDepartmentMembershipLike[] | null
  departmentMemberships?: WorkforceDepartmentMembershipLike[] | null
  role?: string | null
  shift_mode?: string | null
  shiftMode?: string | null
}

function normalizeShiftMode(value: string | null | undefined): WorkforceShiftMode {
  return value === 'self_claim' ? 'self_claim' : 'assigned'
}

function toDepartmentSummary(
  department: WorkforceDepartmentSummaryLike | null | undefined
): WorkforceDepartmentSummary | null {
  if (!(department?.id && department.slug && department.name)) {
    return null
  }

  return {
    id: department.id,
    name: department.name,
    slug: department.slug,
    color: department.color ?? null,
    icon: department.icon ?? null,
    isFieldOps: department.isFieldOps ?? department.is_field_ops ?? false,
    isDefault: department.isDefault ?? department.is_default ?? false,
    defaultShowInSchedule:
      department.defaultShowInSchedule ??
      department.default_show_in_schedule ??
      department.isFieldOps ??
      department.is_field_ops ??
      false,
    defaultShiftMode: normalizeShiftMode(
      department.defaultShiftMode ?? department.default_shift_mode
    ),
  }
}

function getRawDepartmentMemberships(
  member: WorkforceMemberLike | null | undefined
): WorkforceDepartmentMembershipLike[] {
  return member?.departmentMemberships ?? member?.department_members ?? []
}

function getMemberDepartments(
  member: WorkforceMemberLike | null | undefined
): WorkforceDepartmentSummary[] {
  const uniqueDepartments = new Map<string, WorkforceDepartmentSummary>()

  for (const membership of getRawDepartmentMemberships(member)) {
    const department = toDepartmentSummary(membership.department)

    if (!department) {
      continue
    }

    uniqueDepartments.set(department.id, department)
  }

  return Array.from(uniqueDepartments.values())
}

export function getMemberDepartmentIds(
  member: WorkforceMemberLike | null | undefined
): Set<string> {
  const ids = new Set<string>()

  for (const department of getMemberDepartments(member)) {
    ids.add(department.id)
  }

  return ids
}

function getMemberDepartmentSlugs(member: WorkforceMemberLike | null | undefined): Set<string> {
  const slugs = new Set<string>()

  for (const department of getMemberDepartments(member)) {
    slugs.add(department.slug)
  }

  return slugs
}

export function getDepartmentWorkforceDefaults(departments: WorkforceDepartmentDefaultsLike[]): {
  showInSchedule: boolean
  shiftMode: WorkforceShiftMode
} {
  return {
    showInSchedule: departments.some(
      (department) =>
        department.defaultShowInSchedule ??
        department.default_show_in_schedule ??
        department.isFieldOps ??
        department.is_field_ops ??
        false
    ),
    shiftMode: departments.some(
      (department) =>
        normalizeShiftMode(department.defaultShiftMode ?? department.default_shift_mode) ===
        'self_claim'
    )
      ? 'self_claim'
      : 'assigned',
  }
}

function getMemberDepartmentWorkforceDefaults(member: WorkforceMemberLike | null | undefined): {
  showInSchedule: boolean
  shiftMode: WorkforceShiftMode
} {
  return getDepartmentWorkforceDefaults(getMemberDepartments(member))
}

function getMemberShiftMode(member: WorkforceMemberLike | null | undefined): WorkforceShiftMode {
  return normalizeShiftMode(member?.shiftMode ?? member?.shift_mode)
}

function isLegacyFieldWorker(member: WorkforceMemberLike | null | undefined): boolean {
  return (
    member?.customRole?.roleType === 'FIELD_WORKER' ||
    member?.custom_roles?.role_type === 'FIELD_WORKER'
  )
}

export function isWorkerMember(member: WorkforceMemberLike | null | undefined): boolean {
  if (!member) {
    return false
  }

  if (isLegacyFieldWorker(member)) {
    return true
  }

  // Custom OFFICE roles (e.g. Marketing) should NOT be treated as worker members,
  // even if the underlying built-in role is MEMBER with departments
  const customRoleId = member.customRoleId ?? member.custom_role_id
  if (customRoleId) {
    return false
  }

  return member.role === 'MEMBER' && getMemberDepartments(member).length > 0
}

export function isFieldOpsWorker(member: WorkforceMemberLike | null | undefined): boolean {
  if (isLegacyFieldWorker(member)) {
    return true
  }

  // Custom OFFICE roles should not be classified as field ops workers
  const customRoleId = member?.customRoleId ?? member?.custom_role_id
  if (customRoleId) {
    return false
  }

  return (
    member?.role === 'MEMBER' &&
    getMemberDepartments(member).some((department) => department.isFieldOps)
  )
}

export function canSelfClaimShifts(member: WorkforceMemberLike | null | undefined): boolean {
  if (!isWorkerMember(member)) {
    return false
  }

  // Check member-level shift mode first
  if (getMemberShiftMode(member) === 'self_claim') {
    return true
  }

  // Fall back to department-level defaults
  return getMemberDepartmentWorkforceDefaults(member).shiftMode === 'self_claim'
}

export function canClaimShift(
  member: WorkforceMemberLike | null | undefined,
  shift: Pick<DbShift, 'status' | 'user_id' | 'department_id' | 'department'>
): boolean {
  if (!canSelfClaimShifts(member)) {
    return false
  }

  if (shift.status !== 'VACANT' || shift.user_id) {
    return false
  }

  const departmentIds = getMemberDepartmentIds(member)
  const departmentSlugs = getMemberDepartmentSlugs(member)

  if (shift.department_id && departmentIds.has(shift.department_id)) {
    return true
  }

  return !!shift.department?.slug && departmentSlugs.has(shift.department.slug)
}

export function memberBelongsToDepartmentSlug(
  member: WorkforceMemberLike | null | undefined,
  departmentSlug: string
): boolean {
  return getMemberDepartmentSlugs(member).has(departmentSlug)
}
